import React, { useState } from "react";
import { usePricingConfigStore } from "@/stores/usePricingConfigStore";
import { trpc } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, Download } from "lucide-react";
import { exportToExcel } from "@/lib/excel.utils";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  pricingConfigProcessSchema,
  PricingConfigProcessForm,
} from "@/lib/schemas/pricing-config";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfigCombobox } from "@/components/shared/ConfigCombobox";

const ProcessConfigTab = () => {
  const queryClient = useQueryClient();
  const {
    isProcessModalOpen,
    selectedProcessId,
    openProcessModal,
    closeProcessModal,
  } = usePricingConfigStore();
  const { data: processesData, isLoading } = useQuery(
    trpc.pricingConfig.getProcesses.queryOptions(),
  );
  const processes = processesData as unknown as any[];

  const deleteMutation = useMutation({
    ...trpc.pricingConfig.deleteProcess.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã xóa cấu hình công đoạn");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getProcesses.queryKey(),
      });
    },
    onError: (err: any) => {
      toast.error("Lỗi khi xóa công đoạn: " + err.message);
    },
  });

  const handleDelete = (id: string) => {
    if (confirm("Bạn có chắc chắn muốn xóa?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2">
        <Button
          variant="outline"
          onClick={() =>
            exportToExcel(processes || [], [
              { key: "code", label: "Mã", width: 14 },
              { key: "name", label: "Tên công đoạn", width: 28 },
              { key: "type", label: "Loại", width: 14 },
              { key: "unitCost", label: "Đơn giá", width: 14, format: "number" },
              { key: "unitType", label: "Đơn vị tính", width: 14 },
              { key: "minBatchCost", label: "Phí tối thiểu", width: 14, format: "number" },
              { key: "moldPricingType", label: "Khuôn", width: 14 },
              { key: "moldFixedCost", label: "Phí khuôn cố định", width: 16, format: "number" },
            ], "cau_hinh_cong_doan")
          }
        >
          <Download className="w-4 h-4 mr-2" /> Xuất Excel
        </Button>
        <Button onClick={() => openProcessModal()}>
          <Plus className="w-4 h-4 mr-2" /> Thêm công đoạn
        </Button>
      </div>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mã</TableHead>
              <TableHead>Tên công đoạn</TableHead>
              <TableHead>Loại</TableHead>
              <TableHead>Đơn giá</TableHead>
              <TableHead>Đơn vị tính</TableHead>
              <TableHead>Phí tối thiểu</TableHead>
              <TableHead>Khuôn</TableHead>
              <TableHead className="text-right">Thao tác</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : processes?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-10 text-muted-foreground"
                >
                  Chưa có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              processes?.map((process) => (
                <TableRow key={process.id} className="cursor-pointer" onClick={() => openProcessModal(process.id)}>
                  <TableCell className="font-medium">{process.code}</TableCell>
                  <TableCell>{process.name}</TableCell>
                  <TableCell>{process.type}</TableCell>
                  <TableCell>
                    {Number(process.unitCost).toLocaleString()} ₫
                  </TableCell>
                  <TableCell>{process.unitType}</TableCell>
                  <TableCell>
                    {Number(process.minBatchCost).toLocaleString()} ₫
                  </TableCell>
                  <TableCell>
                    {process.moldPricingType !== "FIXED" ? (
                      <Badge variant="outline">{process.moldPricingType}</Badge>
                    ) : Number(process.moldFixedCost) > 0 ? (
                      <Badge variant="secondary">
                        Cố định:{" "}
                        {Number(process.moldFixedCost).toLocaleString()} ₫
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        Không
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(process.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ProcessConfigModal
        isOpen={isProcessModalOpen}
        onClose={closeProcessModal}
        processId={selectedProcessId}
      />
    </div>
  );
};

const ProcessConfigModal = ({
  isOpen,
  onClose,
  processId,
}: {
  isOpen: boolean;
  onClose: () => void;
  processId: string | null;
}) => {
  const queryClient = useQueryClient();
  const { data: processData, isLoading: isLoadingProcess } = useQuery({
    ...trpc.pricingConfig.getProcessById.queryOptions(processId!),
    enabled: !!processId,
  });
  const process = processData as unknown as any;

  const form = useForm<PricingConfigProcessForm>({
    resolver: zodResolver(pricingConfigProcessSchema),
    defaultValues: {
      code: "",
      name: "",
      type: "",
      minBatchCost: 0,
      unitCost: 0,
      unitType: "SHEET",
      moldPricingType: "FIXED",
      baseMoldPrice: 0,
      moldFormula: "",
      moldFixedCost: 0,
      status: "ACTIVE",
    },
  });

  React.useEffect(() => {
    if (process) {
      form.reset({
        ...process,
        minBatchCost: Number(process.minBatchCost) || 0,
        unitCost: Number(process.unitCost) || 0,
        baseMoldPrice: process.baseMoldPrice
          ? Number(process.baseMoldPrice)
          : 0,
        moldFixedCost: Number(process.moldFixedCost) || 0,
      });
    } else {
      form.reset({
        code: "",
        name: "",
        type: "",
        minBatchCost: 0,
        unitCost: 0,
        unitType: "SHEET",
        moldPricingType: "FIXED",
        baseMoldPrice: 0,
        moldFormula: "",
        moldFixedCost: 0,
        status: "ACTIVE",
      });
    }
  }, [process, isOpen, form]);

  const createMutation = useMutation({
    ...trpc.pricingConfig.createProcess.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã thêm cấu hình công đoạn");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getProcesses.queryKey(),
      });
      onClose();
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const updateMutation = useMutation({
    ...trpc.pricingConfig.updateProcess.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã cập nhật cấu hình công đoạn");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getProcesses.queryKey(),
      });
      onClose();
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const onSubmit = (values: PricingConfigProcessForm) => {
    if (processId) {
      updateMutation.mutate({ id: processId, data: values });
    } else {
      createMutation.mutate(values);
    }
  };

  const moldType = form.watch("moldPricingType");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {processId ? "Cập nhật cấu hình công đoạn" : "Thêm công đoạn mới"}
          </DialogTitle>
        </DialogHeader>

        {processId && isLoadingProcess ? (
          <div className="flex justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mã công đoạn</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tên công đoạn</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loại công đoạn</FormLabel>
                      <FormControl>
                        <ConfigCombobox
                          categoryCode="process_types"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Chọn hoặc nhập mới"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unitType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Đơn vị tính</FormLabel>
                      <FormControl>
                        <ConfigCombobox
                          categoryCode="pricing_unit_types"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Chọn đơn vị tính"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unitCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Đơn giá / Đơn vị tính</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="minBatchCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phí tối thiểu / Batch</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="col-span-2 mt-4">
                  <h4 className="text-sm font-semibold mb-2">
                    Cấu hình khuôn (nếu có)
                  </h4>
                  <div className="grid grid-cols-2 gap-4 border p-4 rounded-md bg-slate-50">
                    <FormField
                      control={form.control}
                      name="moldPricingType"
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>Loại tính phí khuôn</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Chọn loại tính phí" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="FIXED">Giá cố định</SelectItem>
                              <SelectItem value="AREA">
                                Theo diện tích (cm2)
                              </SelectItem>
                              <SelectItem value="FORMULA">
                                Tính theo công thức
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {moldType === "FIXED" && (
                      <FormField
                        control={form.control}
                        name="moldFixedCost"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Giá khuôn cố định</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {moldType === "AREA" && (
                      <FormField
                        control={form.control}
                        name="baseMoldPrice"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Giá khuôn / cm2</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                value={field.value || 0}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {moldType === "FORMULA" && (
                      <FormField
                        control={form.control}
                        name="moldFormula"
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel>Công thức tính giá khuôn</FormLabel>
                            <FormControl>
                              <Input
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value)}
                                placeholder="VD: (width + 5) * (length + 5) * 1500"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Hủy
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Đang lưu..."
                    : "Lưu cấu hình"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProcessConfigTab;
